/**
 * Recent activity — Figma `Widget/RecentActivity` (node 12:3103) with
 * `Widget/ActivityRow` (6:408).
 *
 * Measured: the Card shell, then rows at p-8 with a 12px gap, a 28px round tinted
 * bubble, the sentence in `Body/13` `text/primary`, and the relative time in
 * `Caption/12` `text/muted`. Six rows.
 *
 * TWO SOURCES, ONE LIST. `GET /countries/:cc/events` supplies the history the user
 * arrives to; the SSE stream supplies everything after that. They are merged and
 * de-duplicated by event id, because a reconnect can legitimately deliver a row the
 * history already had.
 *
 * THE CONNECTION STATE IS ON SCREEN. A dead feed looks exactly like a quiet
 * afternoon, so "Live" / "Reconnecting…" / "Not updating" is shown next to the
 * title. See `useEventStream` for why the reconnect is hand-rolled.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../../lib/api';
import type { Paged } from '../../lib/inquiries-api';
import { eventToneClasses, presentEvent, relativeTime } from '../../domain/events';
import type { EventRow } from '../../domain/events';
import { Card, EmptyState, SkeletonBlock } from '../../components/ui';
import { cn } from '../../lib/cn';
import { useEventStream } from './useEventStream';
import type { StreamState } from './useEventStream';

const VISIBLE_ROWS = 6;

/** History deep enough that a few live rows do not immediately push it off screen. */
const HISTORY_PAGE = 20;

async function fetchCountryEvents(countryCode: string, signal?: AbortSignal): Promise<EventRow[]> {
  const page = await apiRequest<Paged<EventRow>>(`/countries/${countryCode}/events`, {
    query: { perPage: HISTORY_PAGE },
    signal,
  });
  return page.data;
}

function StreamBadge({ state }: { state: StreamState }): React.JSX.Element {
  const label =
    state === 'live'
      ? 'Live'
      : state === 'connecting'
        ? 'Connecting…'
        : state === 'reconnecting'
          ? 'Reconnecting…'
          : 'Not updating';
  const tone =
    state === 'live'
      ? 'bg-ok-bg text-ok-fg'
      : state === 'failed'
        ? 'bg-danger-bg text-danger-fg'
        : 'bg-subtle text-fg-secondary';

  return (
    <span
      className={cn('inline-flex items-center gap-1.5 rounded-pill px-2 py-0.5 text-label', tone)}
    >
      <span
        aria-hidden="true"
        className={cn('size-1.5 rounded-full bg-current', state === 'live' && 'animate-pulse motion-reduce:animate-none')}
      />
      {label}
    </span>
  );
}

export function RecentActivityWidget({ countryCode }: { countryCode: string }): React.JSX.Element {
  const history = useQuery({
    queryKey: ['events', countryCode, 'recent'],
    queryFn: ({ signal }) => fetchCountryEvents(countryCode, signal),
  });

  const { events: live, state, reconnect } = useEventStream(countryCode);

  const rows = useMemo(() => {
    const seen = new Set<string>();
    const merged: EventRow[] = [];
    for (const row of [...live, ...(history.data ?? [])]) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      merged.push(row);
    }
    merged.sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));
    return merged.slice(0, VISIBLE_ROWS);
  }, [live, history.data]);

  return (
    <Card
      title="Recent activity"
      action={
        state === 'failed' ? (
          <button
            type="button"
            onClick={reconnect}
            className="rounded-sm px-2 py-0.5 text-label font-medium text-indigo hover:bg-indigo-soft"
          >
            Reconnect
          </button>
        ) : (
          <StreamBadge state={state} />
        )
      }
      className="min-h-[388px]"
    >
      {history.isPending ? (
        <div aria-busy="true" aria-label="Loading activity" className="flex flex-col gap-2">
          {Array.from({ length: VISIBLE_ROWS }, (_unused, index) => (
            <div key={index} className="flex items-start gap-3 p-2">
              <SkeletonBlock className="size-7 shrink-0 rounded-full" />
              <div className="flex flex-1 flex-col gap-1.5">
                <SkeletonBlock className="h-3.5 w-4/5" />
                <SkeletonBlock className="h-3 w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : history.isError && rows.length === 0 ? (
        <EmptyState
          title="Activity could not be loaded"
          description="The event feed did not answer. Everything else on this page is unaffected."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nothing has happened yet"
          description="Create an inquiry or log a follow-up and it will appear here, live."
        />
      ) : (
        <ol className="flex flex-1 flex-col">
          {rows.map((row) => {
            const { Icon, tone, text } = presentEvent(row);
            return (
              <li key={row.id} className="flex items-start gap-3 rounded-sm p-2">
                <span
                  className={cn(
                    'inline-flex size-7 shrink-0 items-center justify-center rounded-full',
                    eventToneClasses(tone),
                  )}
                >
                  <Icon aria-hidden="true" className="size-3.5" />
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <p className="text-body13 text-fg">{text}</p>
                  <time dateTime={row.occurred_at} className="text-label text-fg-muted">
                    {relativeTime(row.occurred_at)}
                  </time>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
